import { useEffect, useState } from "react";
import {
  ArrowRight, Link2, QrCode, BarChart3, Copy, CheckCircle2, ExternalLink,
  Loader2, ListPlus, AlertTriangle, RefreshCw, Download,
} from "lucide-react";
import {
  splinkStatus, splinkCreate, splinkBulk, splinkList, splinkStats,
} from "@/lib/api";

const TABS = [
  { key: "create", label: "New link", Icon: Link2 },
  { key: "bulk", label: "Bulk shorten", Icon: ListPlus },
  { key: "list", label: "My links", Icon: BarChart3 },
];

export default function SpLinkPortal() {
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState("create");
  useEffect(() => {
    document.title = "Link Shortener · WebsiteTech";
    splinkStatus().then(setStatus).catch(() => setStatus({ configured: false }));
  }, []);

  return (
    <div className="min-h-screen bg-[#0b1a2b] text-slate-100" data-testid="splink-portal">
      <div className="sticky top-0 z-40 backdrop-blur-lg bg-[#0b1a2b]/85 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-500 to-indigo-500 grid place-items-center">
              <Link2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-sm font-bold">SpLink · Link Shortener + QR</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">
                {status?.reachable
                  ? `${status.tenant?.name} · ${status.environment}`
                  : status?.configured === false
                  ? "Not configured"
                  : "Connecting…"}
              </div>
            </div>
          </div>
          <StatusPill status={status} />
        </div>
        <div className="max-w-6xl mx-auto px-6 flex gap-1 overflow-x-auto pb-2">
          {TABS.map(({ key, label, Icon }) => (
            <button key={key} onClick={() => setTab(key)}
              data-testid={`splink-tab-${key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-t-lg text-xs font-semibold whitespace-nowrap transition ${
                tab === key
                  ? "bg-white/10 text-white border-b-2 border-sky-400"
                  : "text-slate-400 hover:text-white hover:bg-white/5"
              }`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {status?.configured === false && (
          <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-5 mb-6" data-testid="splink-not-configured">
            <AlertTriangle className="w-6 h-6 text-amber-300" />
            <div className="mt-3 text-lg font-bold">SpLink isn't wired up yet</div>
            <div className="mt-1 text-sm text-slate-300">Set <code className="text-amber-300">SPLINK_BASE_URL</code>, <code className="text-amber-300">SPLINK_API_KEY</code> and <code className="text-amber-300">SPLINK_TENANT_SLUG</code> in <code className="text-amber-300">backend/.env</code>, then restart the backend.</div>
          </div>
        )}
        {tab === "create" && <CreateTab />}
        {tab === "bulk" && <BulkTab />}
        {tab === "list" && <ListTab />}
      </div>
    </div>
  );
}


function StatusPill({ status }) {
  if (!status) return null;
  const bad = !status.reachable && status.configured !== false;
  const badge = status.configured === false
    ? { text: "Not configured", cls: "bg-slate-500/20 text-slate-300 border-slate-400/30" }
    : bad
    ? { text: "Unreachable", cls: "bg-rose-500/15 text-rose-300 border-rose-400/30" }
    : status.environment === "production"
    ? { text: "Production", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30" }
    : { text: `Env: ${status.environment || "?"}`, cls: "bg-amber-500/15 text-amber-300 border-amber-400/30" };
  return (
    <div className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-3 py-1 border ${badge.cls}`}
      data-testid="splink-status-pill">
      {badge.text}
    </div>
  );
}


function CreateTab() {
  const [long_url, setLongUrl] = useState("");
  const [alias, setAlias] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    if (!long_url.startsWith("http")) { setError("URL must start with http/https"); return; }
    setBusy(true); setError(""); setResult(null);
    try {
      const r = await splinkCreate({ long_url, alias: alias || null });
      setResult(r);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Shorten failed");
    } finally { setBusy(false); }
  };
  const copy = () => {
    if (result?.short_url) {
      navigator.clipboard.writeText(result.short_url);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="splink-create">
      <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6 space-y-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-300 font-bold mb-1.5">Long URL</div>
          <input value={long_url} onChange={(e) => setLongUrl(e.target.value)}
            placeholder="https://websitetech.pro/tutorials/…"
            data-testid="splink-long-url"
            className="w-full rounded-lg bg-slate-900/70 border border-white/10 focus:border-sky-400 outline-none px-3 py-2.5 text-sm" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-300 font-bold mb-1.5">Custom alias (optional)</div>
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <span className="font-mono">splink.pro/</span>
            <input value={alias} onChange={(e) => setAlias(e.target.value.replace(/[^a-z0-9-_]/gi, ""))}
              placeholder="e.g. commerce-launch"
              data-testid="splink-alias"
              className="flex-1 rounded-lg bg-slate-900/70 border border-white/10 focus:border-sky-400 outline-none px-3 py-2 text-sm font-mono" />
          </div>
          <div className="mt-1 text-[10px] text-slate-500">Reserved words already locked to your tenant: websitetech, webtech, hosting, domain, server, cloud, devops, cms, wordpress, static, spa, pwa, ssl, cdn, backup, migration.</div>
        </div>
        <button onClick={submit} disabled={busy || !long_url}
          data-testid="splink-create-submit"
          className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-white rounded-lg px-4 py-2.5 text-sm font-bold transition">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
          {busy ? "Shortening…" : "Create short URL"}
        </button>
        {error && (
          <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded p-3" data-testid="splink-error">{error}</div>
        )}
      </div>

      {result?.short_url && (
        <div className="rounded-xl border border-emerald-400/40 bg-emerald-500/[0.06] p-6" data-testid="splink-result">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-300" />
              <div className="text-xs uppercase tracking-widest text-emerald-300 font-bold">Short URL ready</div>
            </div>
            <div className="flex gap-2">
              <button onClick={copy} data-testid="splink-copy"
                className="inline-flex items-center gap-1 text-xs bg-white/10 hover:bg-white/20 text-white rounded px-2 py-1 font-semibold">
                {copied ? <><CheckCircle2 className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
              </button>
              <a href={result.short_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs bg-white/10 hover:bg-white/20 text-white rounded px-2 py-1 font-semibold">
                <ExternalLink className="w-3 h-3" /> Open
              </a>
            </div>
          </div>
          <div className="mt-4 text-2xl font-mono font-bold break-all">{result.short_url}</div>
          {result.qr_png && (
            <div className="mt-6 flex gap-6 items-center flex-wrap">
              <img src={result.qr_png} alt="QR code"
                className="w-32 h-32 rounded-lg border border-white/10 bg-white p-2" />
              <div className="text-xs text-slate-400 space-y-1">
                <div>Slug: <span className="font-mono text-white">{result.slug}</span></div>
                <div className="flex gap-2 flex-wrap">
                  <a href={result.qr_png} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200 underline"><Download className="w-3 h-3" /> PNG</a>
                  <a href={result.qr_svg} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200 underline"><Download className="w-3 h-3" /> SVG</a>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function BulkTab() {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const urls = text.split(/\s+/).filter((u) => u.startsWith("http"));

  const submit = async () => {
    if (!urls.length) return;
    setBusy(true); setError(""); setResult(null);
    try {
      const r = await splinkBulk(urls);
      setResult(r);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Bulk shorten failed");
    } finally { setBusy(false); }
  };

  const rows = result?.results || result?.items || [];
  const csv = rows.map((r) => `${r.long_url},${r.short_url || ""},${r.slug || ""}`).join("\n");

  return (
    <div className="max-w-3xl space-y-5" data-testid="splink-bulk">
      <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
        <div className="text-xs uppercase tracking-widest text-sky-300 font-bold">Paste up to 500 URLs — one per line</div>
        <textarea value={text} onChange={(e) => setText(e.target.value)}
          rows={10} placeholder="https://…\nhttps://…"
          data-testid="splink-bulk-urls"
          className="mt-3 w-full rounded-lg bg-slate-900/70 border border-white/10 focus:border-sky-400 outline-none px-3 py-2.5 text-sm font-mono" />
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <button onClick={submit} disabled={busy || urls.length === 0}
            data-testid="splink-bulk-submit"
            className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-white rounded-lg px-4 py-2.5 text-sm font-bold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListPlus className="w-4 h-4" />}
            {busy ? "Shortening…" : `Shorten ${urls.length} URL${urls.length === 1 ? "" : "s"}`}
          </button>
          <div className="text-xs text-slate-400">{urls.length}/500 valid</div>
        </div>
        {error && (
          <div className="mt-3 text-sm text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded p-3">{error}</div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/[0.06] p-4" data-testid="splink-bulk-result">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs uppercase tracking-widest text-emerald-300 font-bold">{rows.length} shortened</div>
            <a href={`data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`}
              download="splink-bulk.csv"
              className="inline-flex items-center gap-1 text-xs bg-white/10 hover:bg-white/20 rounded px-2 py-1 font-semibold">
              <Download className="w-3 h-3" /> CSV
            </a>
          </div>
          <div className="mt-3 max-h-96 overflow-auto text-xs">
            <table className="w-full font-mono">
              <thead className="text-[10px] uppercase text-slate-400"><tr>
                <th className="text-left py-1">Long</th><th className="text-left py-1">Short</th>
              </tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="py-1 pr-3 text-slate-400 truncate max-w-[300px]">{r.long_url}</td>
                    <td className="py-1"><a href={r.short_url} className="text-sky-300 hover:underline">{r.short_url || "—"}</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


function ListTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const load = async () => {
    setLoading(true);
    try {
      const d = await splinkList(100);
      setRows(d.items || d.links || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadStats = async (slug) => {
    try {
      const s = await splinkStats(slug);
      setStats((st) => ({ ...st, [slug]: s }));
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-4" data-testid="splink-list">
      <div className="flex items-center gap-2">
        <button onClick={load} disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 hover:border-white/30 bg-white/5 hover:bg-white/10 px-3 py-2 text-xs font-semibold">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
        <div className="text-xs text-slate-400">{rows.length} links</div>
      </div>
      <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-[10px] uppercase tracking-widest text-slate-400 font-bold">
            <tr>
              <th className="text-left px-4 py-3">Short URL</th>
              <th className="text-left px-4 py-3">Long URL</th>
              <th className="text-right px-4 py-3">Clicks</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.slug} className="border-t border-white/5"
                data-testid={`splink-row-${r.slug}`}>
                <td className="px-4 py-3">
                  <a href={r.short_url} target="_blank" rel="noopener noreferrer"
                    className="font-mono text-sky-300 hover:underline break-all">{r.short_url || r.slug}</a>
                </td>
                <td className="px-4 py-3 text-slate-400 truncate max-w-[280px]">{r.long_url}</td>
                <td className="px-4 py-3 text-right font-mono">{stats[r.slug]?.clicks ?? r.clicks ?? "—"}</td>
                <td className="px-4 py-3 text-[11px] text-slate-500">{(r.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => loadStats(r.slug)}
                    className="text-[11px] text-sky-300 hover:text-sky-200 font-bold">
                    Stats
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">No links yet — create your first in the "New link" tab.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
